import { useState, useEffect, useMemo, useRef } from "react";
import { agentApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import DraggableNode from "../../builder/components/DraggableNode";

// Phase B per agent-detail-viewer-v1.md §10 row 8. Read-only LangGraph DAG
// rendered with the Builder's DraggableNode (mode="readonly" from B5) +
// hand-rolled BFS layered layout (no dagre dep — the graph has at most
// 13 nodes so a simple column layout is sufficient).

const NODE_W = 170;
const NODE_H = 64;
const COL_W = 240;
const ROW_H = 100;

const KIND_COLOR = {
  router:       COLORS.accent,
  guardrail:    COLORS.pink,
  preprocess:   COLORS.cyan,
  llm:          COLORS.accent,
  tools:        COLORS.green,
  verifier:     COLORS.orange,
  fallback:     COLORS.textMuted,
  orchestrator: COLORS.cyan,
  aggregator:   COLORS.cyan,
};

// Exported for unit testing
export function layoutDag(nodes, edges, entry) {
  // BFS from entry — every node gets a depth (column). Slot index within
  // a column orders by id for determinism. Loop edges (back to call_llm
  // etc.) are not followed during BFS but render as curved arrows.
  const depth = new Map();
  const queue = [[entry, 0]];
  while (queue.length) {
    const [id, d] = queue.shift();
    if (depth.has(id) && depth.get(id) <= d) continue;
    depth.set(id, d);
    const out = edges.filter(e => e.from === id && e.kind !== "loop");
    for (const e of out) {
      const nextDepth = d + 1;
      if (!depth.has(e.to) || depth.get(e.to) < nextDepth) {
        queue.push([e.to, nextDepth]);
      }
    }
  }
  // Any node not reached (rare — terminal-only paths) fall to the max depth + 1.
  const maxDepth = Math.max(0, ...Array.from(depth.values()));
  for (const n of nodes) {
    if (!depth.has(n.id)) depth.set(n.id, maxDepth + 1);
  }

  const byDepth = new Map();
  for (const n of nodes) {
    const d = depth.get(n.id);
    if (!byDepth.has(d)) byDepth.set(d, []);
    byDepth.get(d).push(n);
  }
  for (const arr of byDepth.values()) arr.sort((a, b) => a.id.localeCompare(b.id));

  const positions = new Map();
  for (const [d, arr] of byDepth.entries()) {
    arr.forEach((n, idx) => {
      positions.set(n.id, {
        x: 30 + d * COL_W,
        y: 30 + idx * ROW_H,
      });
    });
  }
  return positions;
}

// Exported for unit testing
export function portCenter(pos, side) {
  return {
    x: pos.x + (side === "right" ? NODE_W : 0),
    y: pos.y + NODE_H / 2,
  };
}

// Exported for unit testing
export function arrowPath(from, to) {
  const dx = Math.max(40, (to.x - from.x) / 2);
  return `M${from.x},${from.y} C${from.x + dx},${from.y} ${to.x - dx},${to.y} ${to.x},${to.y}`;
}

const EDGE_COLOR = { static: COLORS.accent, dynamic: COLORS.orange, loop: COLORS.cyan };

export default function GraphTab({ tree, onOpenDrawer }) {
  const supervisor = tree?.agent;
  const experts = tree?.experts || [];
  const isSupervisor = (tree?.agent_kind || "regular") === "supervisor";

  const choices = useMemo(() => {
    if (!supervisor) return [];
    return isSupervisor
      ? [{ id: supervisor.id, label: `${supervisor.name} (supervisor)` }, ...experts.map(e => ({ id: e.id, label: e.name }))]
      : [{ id: supervisor.id, label: supervisor.name }];
  }, [supervisor, experts, isSupervisor]);

  const [selected, setSelected] = useState(supervisor?.id || null);
  useEffect(() => { setSelected(supervisor?.id || null); }, [supervisor?.id]);

  // Per-agent fetched graph cache so flipping the picker doesn't re-fetch
  // a graph already seen on this mount.
  const cacheRef = useRef(new Map());
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!selected) return;
    setErr(null);
    if (cacheRef.current.has(selected)) {
      setGraph(cacheRef.current.get(selected));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    agentApi.getGraph(selected)
      .then(res => {
        if (cancelled) return;
        const g = res?.data || null;
        if (g) cacheRef.current.set(selected, g);
        setGraph(g);
      })
      .catch(e => { if (!cancelled) setErr(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selected]);

  const nodesForLayout = graph?.nodes || [];
  const edges = graph?.edges || [];
  const entry = graph?.entry || nodesForLayout[0]?.id;
  const positions = useMemo(
    () => layoutDag(nodesForLayout, edges, entry),
    [nodesForLayout, edges, entry]
  );

  const tilesNodes = nodesForLayout.map(n => {
    const pos = positions.get(n.id) || { x: 30, y: 30 };
    return {
      id: n.id,
      label: n.label,
      type: n.kind === "llm" ? "llm" : "node",
      icon: "•",
      color: KIND_COLOR[n.kind] || COLORS.accent,
      enabled: n.enabled,
      x: pos.x,
      y: pos.y,
    };
  });

  // Canvas extents — keep some padding for the rightmost column.
  const maxX = Math.max(0, ...tilesNodes.map(t => t.x + NODE_W));
  const maxY = Math.max(0, ...tilesNodes.map(t => t.y + NODE_H));

  const [hoverId, setHoverId] = useState(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      {/* Picker (Supervisor only — single-row strip) */}
      {choices.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {choices.map(c => {
            const active = c.id === selected;
            return (
              <button key={c.id} onClick={() => setSelected(c.id)} style={{
                background: active ? COLORS.accentSoft : "transparent",
                border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
                color: active ? COLORS.text : COLORS.textMuted,
                padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12,
              }}>{c.label}</button>
            );
          })}
        </div>
      )}

      {loading && <div style={{ color: COLORS.textDim, fontSize: 12 }}>Loading graph…</div>}
      {!loading && err && <div style={{ color: COLORS.pink, fontSize: 12 }}>Failed to load graph: {err}</div>}

      {!loading && !err && graph && (
        <div style={{
          position: "relative",
          width: "100%",
          height: maxY + 60,
          minHeight: 400,
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          overflow: "auto",
        }}>
          <div style={{ position: "relative", width: maxX + 60, height: maxY + 60 }}>
            {/* Edges */}
            <svg style={{ position: "absolute", top: 0, left: 0, width: maxX + 60, height: maxY + 60, pointerEvents: "none" }}>
              <defs>
                <marker id="viewer-arrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <path d="M0,0 L8,3 L0,6 Z" fill={COLORS.accent} />
                </marker>
                <marker id="viewer-arrow-dyn" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <path d="M0,0 L8,3 L0,6 Z" fill={COLORS.orange} />
                </marker>
              </defs>
              {edges.map((e, i) => {
                const fromPos = positions.get(e.from);
                const toPos = positions.get(e.to);
                if (!fromPos || !toPos) return null;
                const a = portCenter(fromPos, "right");
                const b = portCenter(toPos, "left");
                const stroke = EDGE_COLOR[e.kind] || COLORS.accent;
                const dashed = e.kind === "dynamic";
                const isReverse = toPos.x < fromPos.x;
                if (isReverse) {
                  // Re-prompt / loop: arc downward.
                  const drop = 80;
                  const path = `M${a.x},${a.y} C${a.x + 40},${a.y + drop} ${b.x - 40},${b.y + drop} ${b.x},${b.y}`;
                  return (
                    <path key={i} d={path} fill="none" stroke={stroke} strokeWidth={1.5}
                      strokeDasharray="4 3" opacity="0.7"
                      markerEnd={dashed ? "url(#viewer-arrow-dyn)" : "url(#viewer-arrow)"} />
                  );
                }
                return (
                  <path key={i} d={arrowPath(a, b)} fill="none"
                    stroke={stroke} strokeWidth={1.5}
                    strokeDasharray={dashed ? "4 3" : "none"}
                    opacity={hoverId && (hoverId === e.from || hoverId === e.to) ? 1 : 0.6}
                    markerEnd={dashed ? "url(#viewer-arrow-dyn)" : "url(#viewer-arrow)"} />
                );
              })}
            </svg>

            {/* Nodes (readonly) */}
            {tilesNodes.map((n, idx) => (
              <div key={n.id}
                onMouseEnter={() => setHoverId(n.id)}
                onMouseLeave={() => setHoverId(prev => prev === n.id ? null : prev)}
              >
                <DraggableNode
                  mode="readonly"
                  node={n}
                  isSelected={false}
                  onClick={() => {
                    if (!onOpenDrawer) return;
                    const liveAgent = (selected === supervisor?.id ? supervisor : experts.find(e => e.id === selected)) || supervisor;
                    const siblings = tilesNodes.map(t => ({
                      kind: `Node: ${t.label}`,
                      payload: {
                        node: t,
                        agent_id: selected,
                        agent_name: liveAgent?.name,
                        config_slice: nodeConfigSlice(t.id, liveAgent),
                      },
                    }));
                    onOpenDrawer({ ...siblings[idx], siblings, index: idx });
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      {!loading && !err && graph && (
        <div style={{ display: "flex", gap: 14, fontSize: 11, color: COLORS.textDim, flexWrap: "wrap" }}>
          <span>solid = static edge</span>
          <span>dashed = Command-driven</span>
          <span>arc = re-prompt loop</span>
          <span>dimmed tile = stage disabled in flow_config</span>
        </div>
      )}
    </div>
  );
}

// Exported for unit testing
export function nodeConfigSlice(nodeId, agent) {
  if (!agent) return null;
  const fc = agent.flow_config || {};
  const map = {
    call_llm:                ["system_prompt", "model", "provider", "temperature", "max_tokens"],
    check_input_guardrails:  ["flow_config.input_guardrails"],
    check_output_guardrails: ["flow_config.output_guardrails"],
    fact_check:              ["flow_config.fact_checker"],
    execute_tools:           ["flow_config.api"],
    process_attachments:     ["flow_config.preprocess.attachment_processing"],
    detect_language:         ["flow_config.preprocess.language_detection"],
    parallel_prepare:        ["flow_config.preprocess"],
    orchestrate_workflow:    ["flow_config.workflow", "metadata_json.workflow_names"],
    aggregate_results:       ["flow_config.workflow"],
  };
  const keys = map[nodeId];
  if (!keys) return null;
  const out = {};
  for (const k of keys) {
    if (k.startsWith("flow_config.")) {
      const path = k.slice("flow_config.".length).split(".");
      let cur = fc;
      for (const seg of path) cur = cur && typeof cur === "object" ? cur[seg] : undefined;
      out[k] = cur ?? null;
    } else if (k.startsWith("metadata_json.")) {
      const sub = k.slice("metadata_json.".length);
      out[k] = (agent.metadata_json || {})[sub] ?? null;
    } else {
      out[k] = agent[k] ?? null;
    }
  }
  return out;
}
